import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Role } from '@/shared/role'
import { sessionAwareStorage, STORAGE_KEYS } from '@/shared/utils/storage'

export interface AuthUser {
  userId: string
  userNm: string
  corpCd: string
  corpNm?: string
  role: Role
  storeCd?: string
  storeNm?: string
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  aiInitToken: string | null
  aiInitTokenExpiresAt: number | null
  user: AuthUser | null
  isLoggedIn: boolean
  setToken: (accessToken: string, refreshToken?: string | null) => void
  setAiInitToken: (token: string, expiresAt: number) => void
  setUser: (user: AuthUser) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      aiInitToken: null,
      aiInitTokenExpiresAt: null,
      user: null,
      isLoggedIn: false,
      setToken: (accessToken, refreshToken) =>
        set((state) => ({
          accessToken,
          refreshToken: refreshToken === undefined ? state.refreshToken : refreshToken,
        })),
      setAiInitToken: (aiInitToken, aiInitTokenExpiresAt) =>
        set({ aiInitToken, aiInitTokenExpiresAt }),
      setUser: (user) => set({ user, isLoggedIn: true }),
      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          aiInitToken: null,
          aiInitTokenExpiresAt: null,
          user: null,
          isLoggedIn: false,
        }),
    }),
    {
      name: STORAGE_KEYS.AUTH,
      storage: sessionAwareStorage,
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        aiInitToken: s.aiInitToken,
        aiInitTokenExpiresAt: s.aiInitTokenExpiresAt,
        user: s.user,
        isLoggedIn: s.isLoggedIn,
      }),
    },
  ),
)
